/**
 * Tiled map JSON helpers (property normalization) and world-space vertices for Tiled objects (nav, etc.).
 * Previously lived under game-engine-service; consolidated here for reuse without pulling in GES.
 */

import type {
  ICoord2D,
  IRawTiledLayer,
  IRawTiledMap,
  IRawTiledObject,
  IRawTiledTilesetEntry,
  ITiledGroupLayer,
  ITiledLayer,
  ITiledMap,
  ITiledObject,
  ITiledProperty,
  ITileSet,
  TiledLayers,
  TiledProperties,
  TiledPropertyValue
} from '@nova/types';

import { GeomUtils } from './geom-utils';

export { validateRawTiledMap } from '@nova/types';

function isTiledPropertyValue(value: unknown): value is TiledPropertyValue {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function isTiledPropertyArray(properties: ITiledProperty[] | TiledProperties): properties is ITiledProperty[] {
  return Array.isArray(properties);
}

export function convertTiledPropsToSimpleKVPair(properties: ITiledProperty[] | TiledProperties | undefined): TiledProperties {
  const retObj: TiledProperties = {};

  if (!properties) {
    return retObj;
  }

  if (!isTiledPropertyArray(properties)) {
    return properties;
  }

  for (const prop of properties) {
    if (isTiledPropertyValue(prop.value)) {
      retObj[prop.name] = prop.value;
    }
  }

  return retObj;
}

export function normalizeTiledObject(object: IRawTiledObject | ITiledObject): ITiledObject {
  const properties = Array.isArray(object.properties)
    ? convertTiledPropsToSimpleKVPair(object.properties)
    : object.properties;
  return {
    ...object,
    class: object.class ?? '',
    properties,
    type: object.type ?? ''
  };
}

export function normalizeTiledObjects(objects: readonly (IRawTiledObject | ITiledObject)[]): ITiledObject[] {
  return objects.map((object) => normalizeTiledObject(object));
}

function requireLayerNumber(value: number | undefined, layerName: string, propertyName: string): number {
  if (value === undefined) {
    throw new Error(`Tiled layer "${layerName}" missing required numeric property: ${propertyName}`);
  }
  return value;
}

function requireTilesetNumber(value: number | undefined, tilesetName: string, propertyName: string): number {
  if (value === undefined) {
    throw new Error(`Tiled tileset "${tilesetName}" missing required numeric property: ${propertyName}`);
  }
  return value;
}

function requireTilesetString(value: string | undefined, tilesetName: string, propertyName: string): string {
  if (value === undefined) {
    throw new Error(`Tiled tileset "${tilesetName}" missing required string property: ${propertyName}`);
  }
  return value;
}

function normalizeTiledLayer(layer: IRawTiledLayer | ITiledLayer): ITiledLayer {
  const properties = Array.isArray(layer.properties)
    ? convertTiledPropsToSimpleKVPair(layer.properties)
    : layer.properties;

  switch (layer.type) {
    case 'tilelayer': {
      if (!Array.isArray(layer.data)) {
        throw new Error(`Tiled tile layer "${layer.name}" must include a data array`);
      }
      return {
        ...layer,
        data: layer.data,
        height: requireLayerNumber(layer.height, layer.name, 'height'),
        properties,
        type: 'tilelayer',
        width: requireLayerNumber(layer.width, layer.name, 'width')
      };
    }
    case 'objectgroup': {
      return {
        ...layer,
        objects: normalizeTiledObjects(layer.objects ?? []),
        properties,
        type: 'objectgroup'
      };
    }
    case 'imagelayer': {
      if (!layer.image) {
        throw new Error(`Tiled image layer "${layer.name}" must include an image`);
      }
      return {
        ...layer,
        image: layer.image,
        properties,
        type: 'imagelayer'
      };
    }
    case 'group': {
      if (!layer.layers) {
        throw new Error(`Tiled group layer "${layer.name}" must include child layers`);
      }
      return {
        ...layer,
        layers: normalizeTiledLayers(layer.layers),
        properties,
        type: 'group'
      };
    }
    default:
      throw new Error('Unsupported Tiled layer type');
  }
}

export function convertTiledNamedArrayToSimpleKVPair(namedArr: readonly IRawTiledLayer[]): TiledLayers {
  const retObj: TiledLayers = {};

  for (const layer of namedArr) {
    retObj[layer.name] = normalizeTiledLayer(layer);
  }

  return retObj;
}

function normalizeTiledLayers(layers: IRawTiledLayer[] | TiledLayers): TiledLayers {
  if (Array.isArray(layers)) {
    return convertTiledNamedArrayToSimpleKVPair(layers);
  }

  const normalizedLayers: TiledLayers = {};
  for (const layerName in layers) {
    if (!Object.prototype.hasOwnProperty.call(layers, layerName)) { continue; }
    const layer = layers[layerName];
    if (layer) {
      normalizedLayers[layerName] = normalizeTiledLayer(layer);
    }
  }
  return normalizedLayers;
}

function normalizeTiledTileset(tileset: IRawTiledTilesetEntry | ITileSet): ITileSet {
  const tilesetName = tileset.name ?? tileset.source ?? '<unnamed>';
  if (tileset.source) {
    throw new Error(`External Tiled tileset "${tileset.source}" must be inlined before map normalization`);
  }

  const properties = Array.isArray(tileset.properties)
    ? convertTiledPropsToSimpleKVPair(tileset.properties)
    : tileset.properties;
  return {
    ...tileset,
    firstgid: requireTilesetNumber(tileset.firstgid, tilesetName, 'firstgid'),
    margin: requireTilesetNumber(tileset.margin, tilesetName, 'margin'),
    name: requireTilesetString(tileset.name, tilesetName, 'name'),
    properties,
    spacing: requireTilesetNumber(tileset.spacing, tilesetName, 'spacing'),
    tileheight: requireTilesetNumber(tileset.tileheight, tilesetName, 'tileheight'),
    tilewidth: requireTilesetNumber(tileset.tilewidth, tilesetName, 'tilewidth')
  };
}

function normalizeTiledTilesets(tilesets: readonly (IRawTiledTilesetEntry | ITileSet)[]): ITileSet[] {
  return tilesets.map((tileset) => normalizeTiledTileset(tileset));
}

export function convertTiledJsonToSimpleKVPairs(tiledMap: IRawTiledMap): ITiledMap {
  const properties = Array.isArray(tiledMap.properties)
    ? convertTiledPropsToSimpleKVPair(tiledMap.properties)
    : tiledMap.properties;
  return {
    ...tiledMap,
    layers: normalizeTiledLayers(tiledMap.layers),
    properties,
    tilesets: normalizeTiledTilesets(tiledMap.tilesets)
  };
}

export function isTiledGroupLayer(layer: ITiledLayer): layer is ITiledGroupLayer {
  return layer.type === 'group';
}

export function findMainNavTiledObject(navObjects: readonly ITiledObject[]): ITiledObject | undefined {
  return navObjects.find((o) => !!o.properties?.main);
}

function getTiledObjectWorldVerticesInternal(
  collObj: ITiledObject,
  roundToInts: boolean | undefined,
  logInvalidShape: boolean
): ICoord2D[] {
  const vertices: ICoord2D[] = [];

  if (collObj.polygon) {
    for (let index = 0; index < collObj.polygon.length; index++) {
      const vertex = collObj.polygon[index];
      vertices.push({
        x: roundToInts ? Math.round(collObj.x + vertex.x) : collObj.x + vertex.x,
        y: roundToInts ? Math.round(collObj.y + vertex.y) : collObj.y + vertex.y
      });
    }
    return vertices;
  }

  if (collObj.width && collObj.height) {
    const rectVerts = GeomUtils.GetTiledRectangleWorldVertices(
      { x: collObj.x, y: collObj.y },
      collObj.width,
      collObj.height,
      collObj.rotation || 0
    );
    for (const v of rectVerts) {
      vertices.push({
        x: roundToInts ? Math.round(v.x) : v.x,
        y: roundToInts ? Math.round(v.y) : v.y
      });
    }
    return vertices;
  }

  if (logInvalidShape) {
    console.error('ONLY POLYGONS & RECTANGLES for NavMesh allowed.');
  }
  return [];
}

/** World vertices for a Tiled polygon or rectangle object (nav holes, etc.). */
export function getTiledObjectWorldVertices(collObj: ITiledObject, roundToInts?: boolean): ICoord2D[] {
  return getTiledObjectWorldVerticesInternal(collObj, roundToInts, true);
}

/** Vertices for the nav object marked `main`, or null if missing / not a polygon or rectangle. */
export function getMainNavPlaneVertices(navObjects: ITiledObject[]): ICoord2D[] | null {
  const main = findMainNavTiledObject(navObjects);
  if (!main) { return null; }
  const verts = getTiledObjectWorldVerticesInternal(main, false, false);
  return verts.length > 0 ? verts : null;
}

/** World-space hole polygons from nav-layer objects other than the `main` plane. */
export function getNavMeshHolePolygons(navObjects: readonly ITiledObject[]): ICoord2D[][] {
  const holes: ICoord2D[][] = [];
  for (const obj of navObjects) {
    if (obj.properties?.main) {
      continue;
    }
    const verts = getTiledObjectWorldVertices(obj);
    if (verts.length >= 3) {
      holes.push(verts);
    }
  }
  return holes;
}

function pointInAnyNavHole(x: number, y: number, holes: readonly ICoord2D[][]): boolean {
  for (const hole of holes) {
    if (GeomUtils.PointInPolygon(x, y, hole)) {
      return true;
    }
  }
  return false;
}

/**
 * Random world-px point inside the main nav boundary and outside navmesh hole polygons.
 * Used for spawn fallback when tiled spawn objects are absent.
 */
export function getRandomPointOnNavWalkable(
  navObjects: readonly ITiledObject[],
  maxAttempts = 400
): ICoord2D | null {
  const boundary = getMainNavPlaneVertices([...navObjects]);
  if (!boundary || boundary.length < 3) {
    return null;
  }

  const holes = getNavMeshHolePolygons(navObjects);
  let minX = boundary[0].x;
  let maxX = boundary[0].x;
  let minY = boundary[0].y;
  let maxY = boundary[0].y;
  for (const p of boundary) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }

  for (let k = 0; k < maxAttempts; k++) {
    const x = minX + (Math.random() * (maxX - minX));
    const y = minY + (Math.random() * (maxY - minY));
    if (GeomUtils.PointInPolygon(x, y, boundary) && !pointInAnyNavHole(x, y, holes)) {
      return { x, y };
    }
  }

  const cx = boundary.reduce((s, p) => s + p.x, 0) / boundary.length;
  const cy = boundary.reduce((s, p) => s + p.y, 0) / boundary.length;
  if (GeomUtils.PointInPolygon(cx, cy, boundary) && !pointInAnyNavHole(cx, cy, holes)) {
    return { x: cx, y: cy };
  }

  return GeomUtils.GetRandomPointInSimplePolygon(boundary, maxAttempts);
}
